import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { ICalendar } from "datebook";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { map, startWith } from "rxjs";

import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";

import { Category, NotificationType } from "../../../apis/networkdashboard/graphql/graphql";
import { CimStatus } from "../../../apis/networkdashboard/static/custom-types";
import { DEFAULT_LANGUAGE } from "../../../app/constants";
import { CimImpactTypePipe } from "../../../helpers/cim-pipe/cim-pipe";
import { slugify } from "../../../helpers/slugify";
import { AnalyticsService } from "../../../services/analytics.service";
import { AnalyticsAction } from "../../../services/analytics.types";
import { AffectedSubscription } from "./components/affected-subscription/affected-subscription";

@Component({
  selector: "malfunction-card",
  templateUrl: "malfunction-card.html",
  styleUrls: ["malfunction-card.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AffectedSubscription, AngularSvgIconModule, CimImpactTypePipe, DatePipe, TranslatePipe],
})
export class MalfunctionCardComponent {
  protected dialog = inject(MatDialog);
  protected translate = inject(TranslateService);
  private analytics = inject(AnalyticsService);
  private currentLanguage = toSignal(
    this.translate.onLangChange.pipe(
      map((e) => e.lang.toUpperCase()),
      startWith((this.translate.getCurrentLang() ?? DEFAULT_LANGUAGE).toUpperCase()),
    ),
    { requireSync: true },
  );

  public showTimeElement = input.required<boolean>();
  public message = input.required<NotificationType>();
  public isEmbedded = input(false);
  public isMajorDisruption = input(false);

  protected title = computed(() => this.message().heading);

  protected languageIdentifier = computed(() => (this.currentLanguage() === "EN" ? "ENG" : "NL"));
  protected affectedSubscriptions = computed(() => this.message().affectedSubscriptions ?? []);
  protected nrOfAffectedServices = computed(() => this.affectedSubscriptions().length);
  protected isMalfunction = computed(() => this.message().category === Category.Malfunction);
  protected isPlannedWork = computed(() => this.message().category === Category.Plannedwork);
  protected startTimestamp = computed(() => this.message().startDate);
  protected endTimestamp = computed(() => this.message().endDate);
  protected planTimestamp = computed(() => this.message().planDate);
  protected localeUpdates = computed(() =>
    this.message().updates.filter((update) => update.language === this.languageIdentifier()),
  );
  protected summary = computed(() => (this.localeUpdates().length > 0 ? this.localeUpdates()[0] : null));
  protected description = computed(() => this.summary()?.description);
  protected updates = computed(() => (this.localeUpdates().length > 1 ? this.localeUpdates().slice(1) : []));
  protected localeIdentifier = computed(() => (this.currentLanguage() === "EN" ? "en-US" : "nl-NL"));
  protected showAffectedSubscriptions = signal(false);

  protected impactsString = computed(() => {
    const uniqueImpacts = new Set(this.affectedSubscriptions().map((p) => p.impactType));
    return Array.from(uniqueImpacts).join(" & ");
  });
  protected isActiveTicket = computed(() => {
    const today = dayjs();
    return (
      dayjs(this.startTimestamp()).isBefore(today)
      && (!this.endTimestamp() || dayjs(this.endTimestamp()).isAfter(today))
      && this.message().status !== CimStatus.CLOSED
    );
  });
  protected isFutureMessage = computed(() => {
    const today = Math.round(new Date().getTime() / 1000);
    if (this.startTimestamp() > today) {
      return true;
    }

    const msgTs = new Date(this.startTimestamp()).getTime();
    if (msgTs > new Date().getTime()) {
      return true;
    }

    return false;
  });

  toggleAffectedSubscriptions() {
    // major disruption tickets have no linked affected subscriptions
    // because they affect all subscriptions.
    // don't toggle here, because it looks weird.
    if (!this.isMajorDisruption()) {
      // Track only on expand (not collapse) to count deliberate ticket inspection.
      if (!this.showAffectedSubscriptions()) {
        this.analytics.trackCurrentFeature(AnalyticsAction.InspectTicket, this.message().category);
      }
      this.showAffectedSubscriptions.update((value) => !value);
    }
  }

  addToCalendar() {
    const icalendar = new ICalendar({
      title: this.title(),
      description: this.description(),
      start: new Date(this.startTimestamp()),
      end: new Date(this.endTimestamp()),
    });
    saveAs(new Blob([icalendar.render()], { type: "text/calendar;charset=utf-8" }), `${slugify(this.title())}.ics`);
  }
}
